/**
 * WhatCanTheySeeAccordion — Collapsible educational section.
 *
 * Summarises current consent state for the first active practitioner.
 * Animated expand/collapse respects reduced motion.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  Animated,
  LayoutAnimation,
  Platform,
  StyleSheet,
  UIManager,
} from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { GlassCard } from '../../glass';

// Enable LayoutAnimation on Android
if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

interface WhatCanTheySeeAccordionProps {
  historyOn: boolean;
  messagesOn: boolean;
  crisisOn: boolean;
}

interface AccordionRowData {
  variant: 'green' | 'red' | 'purple';
  label: string;
  detail: string;
}

export const WhatCanTheySeeAccordion: React.FC<WhatCanTheySeeAccordionProps> = React.memo(
  function WhatCanTheySeeAccordion({ historyOn, messagesOn, crisisOn }) {
    const { t } = useTranslation('practitioner_consent');
    const [isOpen, setIsOpen] = useState(false);
    const chevronRotation = useRef(new Animated.Value(0)).current;

    const rows: AccordionRowData[] = useMemo(() => [
      {
        variant: historyOn ? 'green' : 'red',
        label: t('accordion.history_on.label'),
        detail: t('accordion.history_on.detail'),
      },
      {
        variant: messagesOn ? 'red' : 'green',
        label: messagesOn ? t('accordion.messages_on.label') : t('accordion.messages_off.label'),
        detail: messagesOn
          ? t('accordion.messages_on.detail')
          : t('accordion.messages_off.detail'),
      },
      {
        variant: 'purple',
        label: t('accordion.crisis.label'),
        detail: t('accordion.crisis.detail'),
      },
    ], [historyOn, messagesOn, crisisOn, t]);

    const toggle = useCallback(() => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      Animated.timing(chevronRotation, {
        toValue: isOpen ? 0 : 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
      setIsOpen((prev) => !prev);
    }, [isOpen, chevronRotation]);

    const chevronStyle = useMemo(() => ({
      transform: [
        {
          rotate: chevronRotation.interpolate({
            inputRange: [0, 1],
            outputRange: ['0deg', '180deg'],
          }),
        },
      ],
    }), [chevronRotation]);

    return (
      <GlassCard withChromeBorder={false} noPadding>
        <Pressable
          onPress={toggle}
          style={({ pressed }) => [styles.header, pressed && styles.headerPressed]}
          accessibilityRole="button"
          accessibilityLabel={t('accordion.title')}
          accessibilityState={{ expanded: isOpen }}
        >
          <View style={styles.headerLeft}>
            <Text style={styles.shieldIcon} accessibilityElementsHidden={true}>🛡️</Text>
            <Text style={styles.headerTitle}>{t('accordion.title')}</Text>
          </View>
          <Animated.Text style={[styles.chevron, chevronStyle]} accessibilityElementsHidden={true}>
            ›
          </Animated.Text>
        </Pressable>

        {isOpen && (
          <View style={styles.body}>
            {rows.map((row, idx) => (
              <AccordionRow key={idx} {...row} />
            ))}
          </View>
        )}
      </GlassCard>
    );
  }
);

interface AccordionRowProps {
  variant: 'green' | 'red' | 'purple';
  label: string;
  detail: string;
}

const AccordionRow: React.FC<AccordionRowProps> = ({ variant, label, detail }) => {
  const iconBg =
    variant === 'green'
      ? '#dcfce7'
      : variant === 'red'
      ? '#fee2e2'
      : '#ede9fe';

  const iconColor =
    variant === 'green' ? '#15803d' : variant === 'red' ? '#EF4444' : '#8B5CF6';

  const iconChar =
    variant === 'green' ? '✓' : variant === 'red' ? '✕' : 'ℹ';

  return (
    <View style={styles.row}>
      <View style={[styles.iconCircle, { backgroundColor: iconBg }]}>
        <Text style={[styles.iconChar, { color: iconColor }]}>{iconChar}</Text>
      </View>
      <View style={styles.rowText}>
        <Text style={styles.rowLabel}>{label}</Text>
        <Text style={styles.rowDetail}>{detail}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  headerPressed: {
    opacity: 0.8,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  shieldIcon: {
    fontSize: 14,
    color: '#A78BFA',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#334155',
  },
  chevron: {
    fontSize: 20,
    color: '#A1A1AA',
    fontWeight: '300',
    transform: [{ rotate: '0deg' }],
  },
  body: {
    paddingHorizontal: 16,
    paddingBottom: 14,
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  iconCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  iconChar: {
    fontSize: 11,
    fontWeight: '700',
  },
  rowText: {
    flex: 1,
  },
  rowLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#334155',
  },
  rowDetail: {
    fontSize: 12,
    color: '#A1A1AA',
    marginTop: 1,
    lineHeight: 17,
  },
});
